import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ClusterProvider } from './context/ClusterContext';
import { AuthProvider } from './context/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';
import Dashboard from './pages/Dashboard';
import Servers from './pages/Servers';
import Clusters from './pages/Clusters';
import AddCluster from './pages/AddCluster';
import ProvisionCluster from './pages/ProvisionCluster';
import ImportCluster from './pages/ImportCluster';
import ClusterDetails from './pages/ClusterDetails';
import Catalog from './pages/Catalog';
import Namespaces from './pages/Namespaces';
import Nodes from './pages/Nodes';
import Pods from './pages/Pods';
import Deployments from './pages/Deployments';
import StatefulSets from './pages/StatefulSets';
import DaemonSets from './pages/DaemonSets';
import Jobs from './pages/Jobs';
import CronJobs from './pages/CronJobs';
import Services from './pages/Services';
import Ingresses from './pages/Ingresses';
import PVCs from './pages/PVCs';
import PVs from './pages/PVs';
import StorageClasses from './pages/StorageClasses';
import ConfigMaps from './pages/ConfigMaps';
import Secrets from './pages/Secrets';
import ServiceAccounts from './pages/ServiceAccounts';
import Roles from './pages/Roles';
import ClusterRoles from './pages/ClusterRoles';
import RoleBindings from './pages/RoleBindings';
import ClusterRoleBindings from './pages/ClusterRoleBindings';
import HPA from './pages/HPA';
import ResourceQuotas from './pages/ResourceQuotas';
import LimitRanges from './pages/LimitRanges';
import Settings from './pages/Settings';
import VpnProfiles from './pages/VpnProfiles';
import Login from './pages/Login';
import AlertsPage from './pages/AlertsPage';
import Playbooks from './pages/Playbooks';
import MetricsPage from './pages/MetricsPage';
import EventsPage from './pages/EventsPage';
import AlertSearchPage from './pages/AlertSearchPage';
import GpuPage from './pages/GpuPage';
import InstanceLaunchPage    from './pages/compute/InstanceLaunchPage';
import BuildImagePage        from './pages/compute/BuildImagePage';
import ImagesPage            from './pages/compute/ImagesPage';
import InstanceDetailPage    from './pages/compute/InstanceDetailPage';
import GpuInstanceLaunchPage from './pages/compute/GpuInstanceLaunchPage';
import GpuInstanceDetailPage from './pages/gpu/GpuInstanceDetailPage';
import AppServicesListPage   from './pages/compute/AppServicesListPage';
import MyServicesPage        from './pages/compute/MyServicesPage';
import AppServiceCreatePage  from './pages/compute/AppServiceCreatePage';
import WorkloadDetailPage    from './pages/compute/WorkloadDetailPage';
import CatalogReleaseDetailPage from './pages/CatalogReleaseDetailPage';
import UserManagement from './pages/UserManagement';
import OrgManagement from './pages/OrgManagement';
import GroupManagement from './pages/GroupManagement';
import NamespaceAccess from './pages/NamespaceAccess';
import Sidebar from './components/Sidebar';
import ProtectedRoute from './components/ProtectedRoute';

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <ClusterProvider>
                  <div className="App">
                    <Sidebar />
                    <div className="main-content">
                      <RoutedErrorBoundary>
                      <Routes>
                        <Route path="/" element={<Dashboard />} />
                        <Route path="/servers" element={<Servers />} />
                        <Route path="/clusters" element={<Clusters />} />
                        <Route path="/clusters/add" element={<AddCluster />} />
                        <Route path="/clusters/provision" element={<ProvisionCluster />} />
                        <Route path="/clusters/import" element={<ImportCluster />} />
                        <Route path="/clusters/:clusterId" element={<ClusterDetails />} />
                        <Route path="/catalog" element={<Catalog />} />
                        <Route path="/catalog/:releaseId" element={<CatalogReleaseDetailPage />} />

                        {/* Cluster */}
                        <Route path="/namespaces" element={<Namespaces />} />
                        {/* The sidebar linked here with no route registered, so the
                            path="*" catch-all below silently sent it to the Dashboard. */}
                        <Route path="/nodes" element={<Nodes />} />

                        {/* Workloads */}
                        <Route path="/pods" element={<Pods />} />
                        <Route path="/deployments" element={<Deployments />} />
                        <Route path="/statefulsets" element={<StatefulSets />} />
                        <Route path="/daemonsets" element={<DaemonSets />} />
                        <Route path="/jobs" element={<Jobs />} />
                        <Route path="/cronjobs" element={<CronJobs />} />

                        {/* Network */}
                        <Route path="/services" element={<Services />} />
                        <Route path="/ingresses" element={<Ingresses />} />

                        {/* Storage */}
                        <Route path="/pvcs" element={<PVCs />} />
                        <Route path="/pvs" element={<PVs />} />
                        <Route path="/storage-classes" element={<StorageClasses />} />

                        {/* Configuration */}
                        <Route path="/configmaps" element={<ConfigMaps />} />
                        <Route path="/secrets" element={<Secrets />} />
                        <Route path="/hpa" element={<HPA />} />
                        <Route path="/resource-quotas" element={<ResourceQuotas />} />
                        <Route path="/limit-ranges" element={<LimitRanges />} />

                        {/* Access Control */}
                        <Route path="/service-accounts" element={<ServiceAccounts />} />
                        {/* All four were sidebar links with no route, so the
                            path="*" catch-all sent them to the Dashboard. */}
                        <Route path="/roles" element={<Roles />} />
                        <Route path="/cluster-roles" element={<ClusterRoles />} />
                        <Route path="/role-bindings" element={<RoleBindings />} />
                        <Route path="/cluster-role-bindings" element={<ClusterRoleBindings />} />

                        {/* Monitoring */}
                        <Route path="/alerts" element={<AlertsPage />} />
                        <Route path="/alerts/search" element={<AlertSearchPage />} />
                        <Route path="/playbooks" element={<Playbooks />} />
                        <Route path="/metrics" element={<MetricsPage />} />
                        <Route path="/events" element={<EventsPage />} />
                        <Route path="/gpu" element={<GpuPage />} />
                        <Route path="/gpu/instances/:id" element={<GpuInstanceDetailPage />} />
                        {/* Compute — Instances. The /compute/instances LIST page was folded into
                            My Workloads (bare-Pod deploys now show as a row there, Kind="Pod") —
                            the launch flow and per-instance detail page stay, since deploying a
                            bare Pod and viewing one by id are both still real, separate actions. */}
                        <Route path="/compute" element={<Navigate to="/compute/workloads" replace />} />
                        <Route path="/compute/instances/new" element={<InstanceLaunchPage />} />
                        <Route path="/compute/images" element={<ImagesPage />} />
                        <Route path="/compute/build" element={<BuildImagePage />} />
                        <Route path="/compute/instances/new-gpu" element={<GpuInstanceLaunchPage />} />
                        <Route path="/compute/instances/:id" element={<InstanceDetailPage />} />
                        {/* Compute — Workloads */}
                        <Route path="/compute/workloads" element={<AppServicesListPage />} />
                        <Route path="/compute/workloads/new" element={<AppServiceCreatePage />} />
                        <Route path="/compute/workloads/:id" element={<WorkloadDetailPage />} />
                        <Route path="/compute/services" element={<MyServicesPage />} />
                        {/* Legacy redirects */}
                        <Route path="/compute/app-services" element={<Navigate to="/compute/workloads" replace />} />
                        <Route path="/compute/app-services/new" element={<Navigate to="/compute/workloads/new" replace />} />
                        <Route path="/app-services" element={<Navigate to="/compute/workloads" replace />} />

                        {/* Settings */}
                        <Route path="/settings" element={<Settings />} />
                        <Route path="/vpn-profiles" element={<VpnProfiles />} />

                        {/* Admin */}
                        <Route path="/admin/users" element={<UserManagement />} />
                        <Route path="/admin/orgs" element={<OrgManagement />} />
                        <Route path="/admin/groups" element={<GroupManagement />} />
                        {/* Rancher Projects: which namespaces a non-admin can see. */}
                        <Route path="/admin/namespace-access" element={<NamespaceAccess />} />

                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>
                      </RoutedErrorBoundary>
                    </div>
                  </div>
                </ClusterProvider>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

// Wraps the routed pages so a crash in one page shows an error (with stack)
// inside the app shell instead of whiting out the whole app. Resets on navigation.
function RoutedErrorBoundary({ children }) {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
}

export default App;
